import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { nanoid } from 'nanoid';

// Define message types for better type checking
export interface WebSocketMessage {
  type?: string;
  message?: string;
  username?: string;
  session_id?: string;
  existing_session_id?: string;
  device_info?: any;
  ip_address?: string;
  timestamp?: string;
  allowed?: boolean;
  existed?: boolean;
  password?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  websocketUrl = environment.baseUrl.replace('http', 'ws');

  socket: WebSocket | null = null;
  messagesSubject = new BehaviorSubject<WebSocketMessage | null>(
    null,
  );
  destroySubject = new Subject<void>();
  localSessionId: string | null = null;
  heartbeatInterval: any = null;
  heartbeatIntervalMs = 10000; // Send heartbeat every 10 seconds
  // Response timing tracking
  pingTimeoutMs = 5000; // Wait up to 5s for a pong
  pingTimeout: any = null;
  pingSentAt: number | null = null;
  lastMessageAt = 0;
  latencySubject = new BehaviorSubject<number | null>(null);

  constructor() {
    this.connect();
  }

  ngOnDestroy(): void {
    this.stopHeartbeat();
    this.disconnect();
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  connect(callbackOrSessionId?: (() => void) | string): void {
    let onConnected: (() => void) | undefined = undefined;
    let providedSessionId: string | undefined = undefined;

    if (typeof callbackOrSessionId === 'function') {
      onConnected = callbackOrSessionId;
    } else if (typeof callbackOrSessionId === 'string') {
      providedSessionId = callbackOrSessionId;
    }

    // If the socket is already open under a different session ID (e.g. a temp ID used before
    // login), close it so we can reconnect under the real session ID. Without this, the
    // Redis ws_alive key would be keyed to the temp ID instead of the UserSession's ID,
    // making duplicate-login detection blind to the active session.
    if (
      providedSessionId &&
      this.localSessionId !== providedSessionId &&
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      this.stopHeartbeat();
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.close();
      this.socket = null;
    }

    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      if (this.socket.readyState === WebSocket.OPEN && onConnected) {
        onConnected();
      } else if (
        this.socket.readyState === WebSocket.CONNECTING &&
        onConnected
      ) {
        this.socket.addEventListener('open', () => onConnected!(), {
          once: true,
        });
      }
      return;
    }

    // Get the session ID for this connection
    this.localSessionId = providedSessionId || this.getSessionId();

    // Determine the WebSocket URL
    const websocketUrlPath = this.websocketUrl;
    const wsUrl = `${websocketUrlPath}/ws/session/${this.localSessionId}`;

    this.socket = new WebSocket(wsUrl);
    this.socket.onopen = () => {
      // start heartbeat after connection check ping=>pong
      this.lastMessageAt = Date.now();
      this.startHeartbeat();
      if (onConnected) {
        onConnected();
      }
    };

    this.socket.onmessage = (event: MessageEvent) => {
      const message = JSON.parse(event.data);
      this.lastMessageAt = Date.now();
      // handle pong to compute latency
      if (message?.type === 'pong' && this.pingSentAt) {
        const latency = Date.now() - this.pingSentAt;
        this.latencySubject.next(latency);
        this.pingSentAt = null;
        if (this.pingTimeout) {
          clearTimeout(this.pingTimeout);
          this.pingTimeout = null;
        }
      }
      this.messagesSubject.next(message);
    };

    // Reconnect immediately when the socket closes or errors
    this.socket.onclose = () => {
      this.stopHeartbeat();
      this.connect();
    };

    this.socket.onerror = () => {
      // Ensure the current socket is closed before reconnect
      this.disconnect();
      this.connect();
    };
  }

  disconnect(): void {
    this.stopHeartbeat();

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  closeWebSocket(): void {
    this.disconnect();
  }

  resetMessages(): void {
    this.messagesSubject.next(null);
  }

  send(message: WebSocketMessage): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify(message));
  }

  respondToDuplicateLogin(
    allow: boolean,
    sessionIdWs: string,
    userNameWs?: string,
  ): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    const message = {
      type: 'duplicate_login_response',
      session_id: sessionIdWs,
      allowed: allow,
      timestamp: new Date().toISOString(),
      username: userNameWs,
    };

    this.socket.send(JSON.stringify(message));
  }

  startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      // If socket is open, send ping and measure latency
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        // Avoid overlapping pings if a previous one is still pending
        if (this.pingSentAt == null) {
          this.pingSentAt = Date.now();
          this.send({
            type: 'ping',
            timestamp: new Date().toISOString(),
          });
          // If no pong within timeout, treat as disconnected and reconnect
          if (this.pingTimeout) {
            clearTimeout(this.pingTimeout);
          }
          this.pingTimeout = setTimeout(() => {
            this.disconnect();
            this.connect();
          }, this.pingTimeoutMs);
        }
      } else {
        // If not open, try to (re)connect
        if (
          !this.socket ||
          this.socket.readyState === WebSocket.CLOSED
        ) {
          this.connect();
        }
      }
    }, this.heartbeatIntervalMs);
  }

  stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.pingTimeout) {
      clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
    }
    this.pingSentAt = null;
  }

  getMessagesByType<T extends WebSocketMessage>(
    type: string,
  ): Observable<T> {
    return this.messagesSubject.pipe(
      takeUntil(this.destroySubject),
      filter(
        (message): message is T => !!message && message.type === type,
      ),
    );
  }

  getSessionId(): string {
    let localSessionId = localStorage.getItem('session_id_ws');
    if (!localSessionId) {
      localSessionId = `temp_${Date.now()}_${nanoid(5)}`;
    }
    return localSessionId;
  }

  getLatency$(): Observable<number | null> {
    return this.latencySubject.asObservable();
  }
}
